import React from "react";

import {
  ChevronRight,
  FileText,
  LayoutGrid,
  Sparkles,
  Users,
} from "lucide-react";

import { ACCENT, ColumnHeading, PointList, RailDot } from "./Chrome";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import {
  SECTION_COLUMN_LABEL,
  bandOf,
  buildAccountSynopsis,
} from "../lib/derive";

/**
 * Step 3 of the ExecBridge flow.
 *
 * The section rail across the top doubles as navigation — each dot opens that
 * section's detail modal. The four columns are the synthesis block's own
 * highlights / lowlights / opportunities+risks / recommendations.
 */
const AccountSynopsis: React.FC<{
  accountName: string;
  data?: XFactorDigestData | null;
  onOpenSection: (sectionId: string) => void;
  onOpenCards: () => void;
  onOpenProfiles: () => void;
  onOpenFullBriefing: () => void;
  onOpenExplain: () => void;
}> = ({
  accountName,
  data,
  onOpenSection,
  onOpenCards,
  onOpenProfiles,
  onOpenFullBriefing,
  onOpenExplain,
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const synopsis = React.useMemo(
    () => buildAccountSynopsis(accountName, data),
    [accountName, data]
  );

  const actions: Array<{
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
  }> = [
    {
      icon: <LayoutGrid size={16} />,
      label: "Briefing cards",
      onClick: onOpenCards,
    },
    {
      icon: <Users size={16} />,
      label: "Executive profiles",
      onClick: onOpenProfiles,
    },
    {
      icon: <FileText size={16} />,
      label: "Full briefing",
      onClick: onOpenFullBriefing,
    },
    {
      icon: <Sparkles size={16} />,
      label: "Explainability",
      onClick: onOpenExplain,
    },
  ];

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold" style={{ color: ACCENT }}>
        Business Health
      </h3>

      {/* Section rail */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <div className="flex min-w-max items-stretch">
          <div className="flex flex-1">
            {synopsis.rail.map(stop => (
              <div
                key={stop.sectionId}
                className="flex min-w-[92px] flex-1 flex-col items-center border-r border-gray-100 px-2 py-3 last:border-r-0"
              >
                <button
                  type="button"
                  onClick={() => onOpenSection(stop.sectionId)}
                  className="flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 transition-colors hover:text-gray-900"
                >
                  <span className="truncate">
                    {SECTION_COLUMN_LABEL[stop.sectionId] || stop.label}
                  </span>
                  <ChevronRight size={11} className="shrink-0" aria-hidden />
                </button>
                <div className="mt-2">
                  <RailDot
                    band={bandOf(stop.score, bhThresholds)}
                    missing={stop.missing}
                    onClick={() => onOpenSection(stop.sectionId)}
                    title={
                      stop.missing
                        ? `${SECTION_COLUMN_LABEL[stop.sectionId] || stop.label} — not in this briefing`
                        : `${SECTION_COLUMN_LABEL[stop.sectionId] || stop.label} — health ${
                            stop.score == null
                              ? "not scored"
                              : Math.round(stop.score)
                          }`
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center justify-center gap-1 border-l border-gray-200 px-4 py-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Actions
            </span>
            <div className="flex items-center gap-1">
              {actions.map(a => (
                <button
                  key={a.label}
                  type="button"
                  onClick={a.onClick}
                  title={a.label}
                  aria-label={a.label}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-purple-50 hover:text-[#ad46ff]"
                >
                  {a.icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Four synthesis columns */}
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <ColumnHeading tone="text-green-600">Highlights</ColumnHeading>
          <PointList points={synopsis.highlights} hideChip />
        </div>
        <div>
          <ColumnHeading tone="text-red-500">Lowlights</ColumnHeading>
          <PointList points={synopsis.lowlights} hideChip />
        </div>
        <div>
          <ColumnHeading>Opportunities &amp; risks</ColumnHeading>
          <PointList points={synopsis.opportunitiesAndRisks} hideChip />
        </div>
        <div>
          <ColumnHeading tone="text-gray-500">
            Recommended Actions and Owners
          </ColumnHeading>
          <PointList points={synopsis.recommendations} hideChip />
        </div>
      </div>
    </div>
  );
};

export default AccountSynopsis;
